"use client";

import { format, isAfter, isBefore, isValid, parseISO, startOfDay } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { enUS as enDayPicker, ptBR as ptDayPicker } from "react-day-picker/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import { getLocale } from "@recobra/i18n/runtime";

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  required?: boolean;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
}

export const DatePicker = ({
  value,
  onChange,
  onBlur,
  placeholder,
  id,
  disabled,
  minDate,
  maxDate,
  className,
  required,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: DatePickerProps) => {
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const { dateLocale, calendarLocale } = activeDateLocales();
  const selectedDate = parseSelectableDate(value);

  const onDateSelect = (date: Date | undefined) => {
    onChange?.(date ? format(date, "yyyy-MM-dd") : undefined);
    setCalendarOpen(false);
  };

  const placeholderText = placeholder ?? m.common_date_placeholder();

  return (
    <Popover
      open={calendarOpen}
      onOpenChange={(next) => {
        setCalendarOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-required={required}
        aria-invalid={ariaInvalid}
        render={
          <Button
            variant="outline"
            data-empty={!selectedDate}
            className={cn(
              "w-full justify-start text-left data-[empty=true]:text-muted-foreground",
              className,
            )}
          />
        }
      >
        {selectedDate ? format(selectedDate, "P", { locale: dateLocale }) : placeholderText}
        <CalendarIcon data-icon="inline-end" className="ml-auto text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          required={required}
          locale={calendarLocale}
          selected={selectedDate}
          onSelect={onDateSelect}
          defaultMonth={selectedDate ?? new Date()}
          disabled={(date) => isOutsideRange(date, minDate, maxDate)}
          captionLayout="dropdown"
          startMonth={minDate ?? EARLIEST_SELECTABLE_MONTH}
          endMonth={maxDate ?? latestSelectableMonth()}
        />
      </PopoverContent>
    </Popover>
  );
};

const activeDateLocales = () =>
  getLocale() === "en"
    ? { dateLocale: enUS, calendarLocale: enDayPicker }
    : { dateLocale: ptBR, calendarLocale: ptDayPicker };

/** An unparseable or absent stored value leaves the picker empty rather than showing `Invalid Date`. */
const parseSelectableDate = (value: string | undefined) => {
  if (!value) return undefined;
  const parsed = parseISO(value);

  return isValid(parsed) ? parsed : undefined;
};

const isOutsideRange = (date: Date, minDate: Date | undefined, maxDate: Date | undefined) => {
  const day = startOfDay(date);

  return Boolean(
    (minDate && isBefore(day, startOfDay(minDate))) ||
    (maxDate && isAfter(day, startOfDay(maxDate))),
  );
};

const EARLIEST_SELECTABLE_MONTH = new Date(1900, 0, 1);
const latestSelectableMonth = () => new Date(new Date().getFullYear() + 10, 11, 31);
